import { useState } from 'react'
import { TableShell } from '../../components/molecules/TableShell'
import { Alert } from '../../components/atoms/Alert'
import { Badge } from '../../components/atoms/Badge'
import { Button } from '../../components/atoms/Button'
import { Spinner } from '../../components/atoms/Spinner'
import { Th, Td } from '../../components/molecules/Table'
import { Paginator } from '../../components/molecules/Paginator'
import { UserTypeFormModal } from './UserTypeFormModal'
import { useUserTypes, type UserTypeDto } from '../../../viewmodels/useUserTypes'
import { useAuth } from '../../../context/AuthContext'
import { SCREENCODES } from '../../../constants/screen.constants'

/**
 * /iam/user-types — list + create/edit/delete.
 *
 * Per .claude/ui/07-per-screen-flows-iam.md §1–§10.
 *
 * `is_system` rows (admin, customer, sale, marketing, warehouse_*) are
 * marked with a "System" badge. Their `code` is immutable and the
 * delete button is hidden — DB-level checks also block these.
 *
 * Element-level permission gating (iam:create / iam:update / iam:delete)
 * will plug in via PermissionGate once that component exists; until then
 * the screen relies on the screen-level admin guard.
 */
export function UserTypesPage(): JSX.Element {
  const {
    items,
    groups,
    total,
    page,
    pageSize,
    isLoading,
    error,
    setPage,
    reload,
    create,
    update,
    remove
  } = useUserTypes()
  const { can } = useAuth()
  const [modalMode, setModalMode] = useState<'closed' | 'create' | 'edit'>('closed')
  const [editTarget, setEditTarget] = useState<UserTypeDto | null>(null)
  const [deletingCode, setDeletingCode] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const openCreate = (): void => {
    setEditTarget(null)
    setModalMode('create')
  }
  const openEdit = (row: UserTypeDto): void => {
    setEditTarget(row)
    setModalMode('edit')
  }
  const closeModal = (): void => setModalMode('closed')

  const handleDelete = async (row: UserTypeDto): Promise<void> => {
    const confirmMsg = `Delete user type "${row.code}"? This cannot be undone.`
    if (typeof window !== 'undefined' && !window.confirm(confirmMsg)) return
    setDeletingCode(row.code)
    setDeleteError(null)
    const res = await remove(row.code)
    setDeletingCode(null)
    if (!res.ok) setDeleteError(res.message)
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between gap-4">
        <h1 className="font-heading text-h2 text-charcoal">User Types</h1>
        {can(SCREENCODES.IAMUSERTYPES, 'add') ? <Button onClick={openCreate}>Add</Button> : null}
      </header>

      {deleteError !== null ? <Alert tone="error">{deleteError}</Alert> : null}
      {error !== null ? (
        <Alert tone="error">
          {error}{' '}
          <button
            type="button"
            className="underline underline-offset-2 hover:text-charcoal"
            onClick={() => void reload()}
          >
            Retry
          </button>
        </Alert>
      ) : null}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner />
        </div>
      ) : items.length === 0 ? (
        <EmptyState onAdd={can(SCREENCODES.IAMUSERTYPES, 'add') ? openCreate : null} />
      ) : (
        <>
          <Table
            items={items}
            onEdit={openEdit}
            onDelete={(row) => void handleDelete(row)}
            deletingCode={deletingCode}
          />
          <Paginator
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            disabled={isLoading}
          />
        </>
      )}

      <UserTypeFormModal
        open={modalMode !== 'closed'}
        mode={modalMode === 'edit' ? 'edit' : 'create'}
        initial={editTarget}
        groups={groups}
        onClose={closeModal}
        onSubmit={async (input) => {
          if (modalMode === 'edit' && editTarget !== null) {
            return update(editTarget.code, input)
          }
          return create(input as Parameters<typeof create>[0])
        }}
      />
    </div>
  )
}

function EmptyState({ onAdd }: { readonly onAdd: (() => void) | null }): JSX.Element {
  return (
    <div className="text-center py-16 border border-border rounded-md bg-parchment/40">
      <p className="font-heading text-h3 text-charcoal">No user types defined</p>
      <p className="font-body text-body-sm text-text-secondary mt-2">
        Add the first user type to start binding operators + customers to landing paths.
      </p>
      {onAdd !== null ? (
        <div className="mt-4">
          <Button onClick={onAdd}>Add</Button>
        </div>
      ) : null}
    </div>
  )
}

function Table({
  items,
  onEdit,
  onDelete,
  deletingCode
}: {
  readonly items: UserTypeDto[]
  readonly onEdit: (row: UserTypeDto) => void
  readonly onDelete: (row: UserTypeDto) => void
  readonly deletingCode: string | null
}): JSX.Element {
  const { can } = useAuth()
  const canEdit = can(SCREENCODES.IAMUSERTYPES, 'edit')
  const canDelete = can(SCREENCODES.IAMUSERTYPES, 'delete')
  return (
    <TableShell>
      <table className="w-full font-body text-body-sm">
        <thead className="bg-parchment/50 text-text-secondary">
          <tr>
            <Th>Code</Th>
            <Th>Name</Th>
            <Th>Landing path</Th>
            <Th>Active</Th>
            <Th>Users</Th>
            <Th>Groups</Th>
            <Th className="text-right">Actions</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((row) => (
            <tr key={row.code} className="border-t border-border">
              <Td>
                <span className="font-mono">{row.code}</span>
                {row.is_system ? (
                  <span className="ml-2 inline-block align-middle">
                    <Badge tone="info">System</Badge>
                  </span>
                ) : null}
              </Td>
              <Td>{row.name}</Td>
              <Td><span className="font-mono text-text-secondary">{row.landing_path ?? '—'}</span></Td>
              <Td>{row.is_active === 1 ? <Badge tone="success">Active</Badge> : <Badge tone="warning">Inactive</Badge>}</Td>
              <Td>{row.bound_user_count}</Td>
              <Td>{row.group_ids.length}</Td>
              <Td className="text-right whitespace-nowrap">
                {canEdit ? (
                  <Button size="sm" variant="secondary" onClick={() => onEdit(row)}>
                    Edit
                  </Button>
                ) : null}
                {canDelete && !row.is_system ? (
                  <>
                    {' '}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onDelete(row)}
                      disabled={deletingCode === row.code || row.bound_user_count > 0}
                    >
                      {deletingCode === row.code ? 'Deleting…' : 'Delete'}
                    </Button>
                  </>
                ) : null}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableShell>
  )
}
